import { observable, action, makeObservable } from 'mobx';
import { AxiosResponse } from 'axios';

import { defaultThemeSettings, getParentTheme } from 'shared/helpers';
import { baseHttp, http } from 'kioskApp/api';
import { ThemeType, WizardStep } from 'shared/interfaces/settings';

export class ErrorModel {
    source: string = '';
    whatHappened: string = '';
    whatToDo: string = '';
    keywords: string[] = [];
}

export interface ErrorDescription extends ErrorModel {
    code: string;
    message: string;

    httpCode: number;
    method: string | undefined;
}

export class ConfigurationModel {
    @observable
    public initialized: boolean = false;
    @observable
    public errors: any = {};
    @observable
    public theme: ThemeType;
    public steps: WizardStep[] = [];

    constructor() {
        makeObservable(this);
    }

    @action
    setValue(value: any) {
        Object.assign(this, value);
    }

    @action
    async init() {
        if (this.initialized) {
            return;
        }

        const errors = baseHttp
            .get('/configurations/errors.json')
            .then((response) =>
                this.setValue({
                    errors: response.data,
                })
            );

        const theme = getParentTheme().then((parentTheme) => {
            if (parentTheme) {
                this.setValue({
                    theme: parentTheme,
                });
            } else {
                this.setValue({
                    theme: defaultThemeSettings,
                });
            }
        });

        const steps = http
            .get<WizardStep[]>('/api/account/steps')
            .then((response) =>
                this.setValue({
                    steps: response.data,
                })
            );

        await Promise.all([errors, theme, steps]);

        this.setValue({
            initialized: true,
        });
    }

    /**
     * Provides description of error according to error map
     * TODO: simplify cascade error parsing
     *
     * @param {AxiosResponse} response
     * @returns {ErrorDescription}
     * @memberof ConfigurationModel
     */
    parseError(response: AxiosResponse): ErrorDescription {
        const errorCodes =
            this.errors[response.status.toString()] || this.errors[''];

        const responseError =
            response.data &&
            response.data.errors &&
            response.data.errors.length > 0
                ? response.data.errors[0]
                : { code: '', message: '' };

        let error = Object.assign({}, this.errors[''][''], errorCodes['']);

        if (errorCodes.hasOwnProperty(responseError.code)) {
            Object.assign(error, errorCodes[responseError.code]);
        }

        return {
            source: error.source,
            whatHappened: error.whatHappened,
            whatToDo: error.whatToDo,
            keywords: error.keywords,

            code: responseError.code,
            message: responseError.message,

            httpCode: response.status,
            method: response.config.method,
        };
    }
}

export default ConfigurationModel;
